"use client";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { PopoverTrigger } from "@nextui-org/react";
import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { useMediaQuery } from "react-responsive";

import { hasGoldenVisa } from "@/config/additionalSettings.config";
import { CurrencyValue } from "@/constants/propertyConstants";
import {
  getRedirectUrl,
  getRedirectUrlWithParams,
  normalizePathByDealType,
} from "@/data/helper/getRedirectUrl";
import { DealType } from "@/enums/dealType";
import { FeatureFlags } from "@/enums/feature-flags";
import { useAutocompleteLocationOptions } from "@/hooks/useAutocompleteLocationOptions";
import { useUserRole } from "@/hooks/useUserRole";
import { Link, useRouter } from "@/i18n/routing";
import IconAllFilters from "@/icons/allFilter.svg";
import IconDown from "@/icons/arrow_down-red.svg";
import IconArea from "@/icons/content/property/area.svg";
import IconRooms from "@/icons/content/property/door.svg";
import IconExclusive from "@/icons/exclusiveFilter.svg";
import IconKeywords from "@/icons/icon_keywords.svg";
import IconPropertyType from "@/icons/icon_property_type.svg";
import IconPhone from "@/icons/phone.svg";
import IconPrice from "@/icons/price.svg";
import IconSearch from "@/icons/search.svg";
import IconFloor from "@/icons/stairs.svg";
import IconActive from "@/icons/ui/buttons/activeBtn.svg";
import IconClearFilter from "@/icons/ui/buttons/close-with-circle.svg";
import IconCalendar from "@/icons/ui/forms/calendar.svg";
import IconUser from "@/icons/ui/forms/person.svg";
import { SearchService } from "@/services/search.service";
import { useCurrencyStore } from "@/store/currency.store";
import { Filter } from "@/types/SearchFilter.types";
import RealtorOnly from "@/ui/Common/RealtorOnly";
import { Feature } from "@/ui/Feature";
import { cn } from "@/utils/cn";
import { formatRange } from "@/utils/formatRange";
import { initialFilterValue } from "@/utils/initialFilterValue";
import { logger } from "@/utils/logger";
import { parseQueryToFilters } from "@/utils/parseQueryToFilters";
import { buildQueryStringFromFilters } from "@/utils/queryString";
import { saveScroll } from "@/utils/scrollRestoration";
import { serializeFiltersToQuery } from "@/utils/serializeFiltersToQuery";
import {
  summarizeCountFilters,
  summarizeFilters,
} from "@/utils/summarizeFilters";
import { validateString } from "@/utils/validateCustomId";

import { SORT_KEY } from "../SearchResultHeader";

import SearchLocationClassic from "./SearchLocationClassic";
import SearchLocationSmart from "./SearchLocationSmart";
import { Popover, PopoverContent } from "./SearchPopover";
import styles from "./style.module.scss";

const SearchAll = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchAll"),
);
const SearchDateRange = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchDateRange"),
);
const SearchFloor = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchFloor"),
);
const SearchPrice = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchPrice"),
);
const SearchRoom = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchRoom"),
);
const SearchSquare = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchSquare"),
);
const SearchType = dynamic(
  () => import("@/ui/SearchPanel/SearchModals/SearchType"),
);

const SearchGoldenVisa = dynamic(
  () => import("@/ui/SearchPanel/GoldenVisaButton/GoldenVisaButton"),
);
const SearchExclusive = dynamic(() => import("./SearchModals/SearchExclusive"));
const SearchPhone = dynamic(() => import("./SearchModals/SearchPhone"));
const SearchRealtor = dynamic(() => import("./SearchModals/SearchRealtor"));

type ShowPopUp = (ref: RefObject<HTMLElement | null>, name: string) => void;

const enum EnumPopUpName {
  location = "location",
  propertyType = "propertyType",
  price = "price",
  area = "area",
  floor = "floor",
  rooms = "rooms",
  exclusive = "exclusive",
  publicationDate = "publicationDate",
  addedDate = "addedDate",
  phone = "phone",
  realtor = "realtor",
  allFilters = "allFilters",
}

interface Props {
  dealType: DealType;
  initialFilters?: Partial<Filter>;
  header?: string;
  localizedLocationString?: string;
  isCatalogPage?: boolean;
}

interface LocationSearchForm {
  locationSearch: string;
}

export const SearchPanel = ({
  dealType = DealType.PRIMARY,
  initialFilters: propInitialFilters,
  header,
  localizedLocationString,
  isCatalogPage,
}: Props) => {
  const t = useTranslations();
  const translateProperty = useTranslations("PropertyType");
  const locale = useLocale();
  const { currency, formatCurrencySymbol } = useCurrencyStore();
  const { isRealtor } = useUserRole();
  const searchParams = useSearchParams();
  const router = useRouter();
  const path = usePathname();
  const [searchTitle, setSearchTitle] = useState("");

  // Form setup for location search
  const form = useForm<LocationSearchForm>({
    defaultValues: {
      locationSearch: "",
    },
  });

  const isMobile = useMediaQuery({
    query: "(max-width: 767px)",
  });

  const hasSearchParams = useMemo(
    () =>
      searchParams?.toString()?.length && searchParams.toString().length > 0,
    [searchParams],
  );

  const initialFilters = useMemo(() => {
    try {
      if (hasSearchParams && searchParams) {
        const computedFilters = parseQueryToFilters(
          searchParams,
          dealType,
          propInitialFilters?.region,
        );

        if (
          isCatalogPage &&
          !computedFilters.propertyType?.length &&
          propInitialFilters?.propertyType?.length
        ) {
          computedFilters.propertyType = propInitialFilters?.propertyType;
        }

        return computedFilters;
      }

      if (propInitialFilters && Object.keys(propInitialFilters).length > 0) {
        return { ...initialFilterValue(dealType), ...propInitialFilters };
      }
    } catch (error) {
      console.error("Error parsing search params:", error);
    }
    return initialFilterValue(dealType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.toString(), dealType, propInitialFilters, hasSearchParams]);

  const [activeDealType, setDealType] = useState(dealType);
  const [selectedFilters, setSelectedFilters] =
    useState<Filter>(initialFilters);
  console.log("initialFilters", initialFilters);

  // Инициализация selectedText на основе initialFilters
  const [selectedText, setSelectedText] = useState(() => {
    return initialFilters.titleOrDescription || initialFilters.customId || "";
  });

  // Watch form value for location search
  const watchedLocationSearch = form.watch("locationSearch");

  const {
    data: searchHelpsData,
    error: searchHelpsError,
    refetch,
  } = useQuery({
    queryKey: ["searchHelps", watchedLocationSearch],
    queryFn: () =>
      watchedLocationSearch &&
      watchedLocationSearch.length >= 1 &&
      SearchService.showSearchHelps({ keyword: watchedLocationSearch }),
  });

  const { data: regionsListData, error: regionsListError } = useQuery({
    queryKey: ["getRegions"],
    queryFn: () => SearchService.getRegions(),
  });

  const locationOptions = useAutocompleteLocationOptions(
    regionsListData,
    locale,
  );

  const groupSearchBlock = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLButtonElement>(null);
  const areaRef = useRef<HTMLButtonElement>(null);
  const floorRef = useRef<HTMLButtonElement>(null);
  const roomsRef = useRef<HTMLButtonElement>(null);
  const exclusiveRef = useRef<HTMLButtonElement>(null);
  const publicationDateRef = useRef<HTMLButtonElement>(null);
  const addedDateRef = useRef<HTMLButtonElement>(null);
  const phoneRef = useRef<HTMLButtonElement>(null);
  const realtorRef = useRef<HTMLButtonElement>(null);
  const allRef = useRef<HTMLButtonElement>(null);
  const inputLocationRef = useRef<HTMLInputElement>(null);
  const typeLocationRef = useRef<HTMLDivElement>(null);

  const [activePopUp, setActivePopUp] = useState<EnumPopUpName | "">("");

  // Add event listener for browser back button when popover is open
  useEffect(() => {
    const handlePopState = () => {
      if (activePopUp !== "") {
        closePopUp();
      }
    };

    if (activePopUp !== "") {
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [activePopUp]);

  const handleClearFilter = () => {
    logger.info(
      `[SearchPanel] handleClearFilter: current path from usePathname(): ${path}`,
    );

    localStorage.removeItem(SORT_KEY);

    setSelectedFilters(initialFilterValue(dealType, locationOptions.regions));
    setSelectedText("");
    form.setValue("locationSearch", "");

    const computedUrl = normalizePathByDealType(path, !!isCatalogPage);

    router.replace(computedUrl || "/", {
      scroll: false,
    });
  };

  const handleClearSearchLocationFilter = () => {
    // Close popover immediately to prevent any flickering
    setActivePopUp("");

    // Build cleared filters without location data
    const clearedFilters = {
      ...selectedFilters,
      region: [],
      city: [],
      district: [],
      microdistrict: [],
      street: [],
      metroStation: [],
    };

    // Redirect immediately - this will reload the page so no need to update other state
    const queryString = buildQueryStringFromFilters(clearedFilters);

    const computedUrl = isCatalogPage
      ? normalizePathByDealType(path, isCatalogPage)
      : path;

    router.replace(`${computedUrl}?${queryString}`, {
      scroll: false,
    });
  };

  const handleChangeTitle = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement;
    setSelectedText(value);

    if (validateString(value)) {
      setSelectedFilters((prev) => ({
        ...prev,
        customId: value,
        titleOrDescription: "",
      }));
      return;
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        customId: "",
        titleOrDescription: value,
      }));
    }
  };

  const handleChangeLocation = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement;
    setSearchTitle(value);
  };

  const handleSearchClick = (
    filters?: Filter,
    type?: string,
    scrollToTop: boolean = false,
  ) => {
    const baseFilters = filters ?? selectedFilters;

    const updatedFilters: Partial<Filter & { sort: string }> = {
      ...baseFilters,
    };

    const stripLocale = (p: string) =>
      p.startsWith(`/${locale}`) ? p.replace(`/${locale}`, "") : p;

    const initialRegion =
      searchParams.get("region") ??
      searchParams.get("region[]") ??
      filters?.region?.[0];

    const initialCity =
      searchParams.get("city") ??
      searchParams.get("city[]") ??
      filters?.city?.[0];

    // Preserve sort from URL
    const sortFromUrl = searchParams.get("sort");

    if (sortFromUrl) {
      updatedFilters.sort = sortFromUrl;
    }

    if (type !== "location" && !initialRegion) {
      delete updatedFilters.region;
    }

    // Reset page when applying filters
    delete updatedFilters.page;

    if (isCatalogPage) {
      if (initialCity && !initialRegion && regionsListData?.length) {
        const targetRegion = regionsListData.find((region) =>
          region.cities.some(
            (c) => c.name.en.toLowerCase() === initialCity.toLowerCase(),
          ),
        );

        if (targetRegion) {
          updatedFilters.region = [targetRegion.name.en];
        }
      }

      // When on landing page (passed via prop), redirect to search page with filters
      // Landing pages: /ru/primary/apartment/athens, /ru/buy/houses, /ru/rent/villa
      // Search pages: /ru/developers, /ru/buy, /ru/rent
      router.push(
        {
          pathname: getRedirectUrl(activeDealType),
          query: serializeFiltersToQuery(updatedFilters),
        },
        {
          scroll: scrollToTop,
        },
      );
      return;
    }

    if (type === "location") {
      router.push(`${path}?${buildQueryStringFromFilters(updatedFilters)}`, {
        scroll: scrollToTop,
      });
      return;
    }

    router.push(
      {
        pathname: stripLocale(path),
        query: serializeFiltersToQuery(updatedFilters),
      },
      {
        scroll: type === "price" ? false : scrollToTop,
      },
    );
  };

  const handleChangeDealType = (dealType: DealType) => {
    setDealType(dealType);

    saveScroll(dealType);

    router.push(
      {
        pathname: getRedirectUrl(dealType),
      },
      {
        scroll: false,
      },
    );
  };

  const closePopUp = useCallback(() => {
    setActivePopUp("");
  }, []);

  const handleShowPopup: ShowPopUp = (ref, name) => {
    if (!ref?.current) {
      return;
    }

    const rect = ref.current.getBoundingClientRect();

    // For mobile fullscreen modals, don't scroll
    const isFullscreenModal =
      isMobile &&
      (name === EnumPopUpName.location || name === EnumPopUpName.allFilters);

    const showPopUp = () => {
      setActivePopUp(name as EnumPopUpName);
    };

    if (!isFullscreenModal) {
      const targetScrollY = rect.top + window.scrollY - 20;

      // If we are already close to the target, open popup immediately.
      // Otherwise, scroll and wait for the scroll to end.
      if (Math.abs(window.scrollY - targetScrollY) < 1) {
        showPopUp();
      } else {
        // Cross-browser solution for scroll end detection
        let scrollTimeout: NodeJS.Timeout;
        const scrollListener = () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            showPopUp();
            window.removeEventListener("scroll", scrollListener);
          }, 100); // 100ms delay to detect scroll end
        };

        window.addEventListener("scroll", scrollListener);
        window.scrollTo({
          top: targetScrollY,
          behavior: "smooth",
        });
        // Ensure the listener is removed if scrolling completes very fast or doesn't trigger
        scrollTimeout = setTimeout(() => {
          showPopUp();
          window.removeEventListener("scroll", scrollListener);
        }, 1000); // Fallback timeout
      }
    } else {
      // For fullscreen modals, open immediately without scrolling.
      showPopUp();
    }
  };

  const handleChangeCurrency = (currency: any) => {
    setSelectedFilters((prev: Filter) => {
      return {
        ...prev,
        currency: currency.value,
        priceCurrency: currency.value, // Add priceCurrency for new API
      };
    });
  };

  useEffect(() => {
    handleChangeCurrency(currency);
  }, [currency]);

  const locationValues = useMemo(() => {
    return [
      ...(selectedFilters.region || []),
      ...(selectedFilters.city || []),
      ...(selectedFilters.district || []),
      ...(selectedFilters.microdistrict || []),
      ...(selectedFilters.street || []),
      ...(selectedFilters.metroStation || []),
    ];
  }, [
    selectedFilters.region,
    selectedFilters.city,
    selectedFilters.district,
    selectedFilters.microdistrict,
    selectedFilters.street,
    selectedFilters.metroStation,
  ]);

  // Подсветка иконок
  const isLocationActive = useMemo(() => {
    return (
      (selectedFilters.region && selectedFilters.region.length > 0) ||
      (selectedFilters.city && selectedFilters.city.length > 0) ||
      (selectedFilters.district && selectedFilters.district.length > 0) ||
      (selectedFilters.microdistrict &&
        selectedFilters.microdistrict.length > 0) ||
      (selectedFilters.street && selectedFilters.street.length > 0) ||
      (selectedFilters.metroStation && selectedFilters.metroStation.length > 0)
    );
  }, [
    selectedFilters.region,
    selectedFilters.city,
    selectedFilters.district,
    selectedFilters.microdistrict,
    selectedFilters.street,
    selectedFilters.metroStation,
  ]);

  const isPropertyTypeActive = useMemo(() => {
    return selectedFilters.objectType && selectedFilters.objectType.length > 0;
  }, [selectedFilters.objectType]);

  const isKeywordsActive = useMemo(() => {
    return !!selectedText;
  }, [selectedText]);

  const isPriceActive = useMemo(() => {
    return (
      !!selectedFilters.totalPriceMin ||
      !!selectedFilters.totalPriceMax ||
      !!selectedFilters.pricePerSqMMin ||
      !!selectedFilters.pricePerSqMMax
    );
  }, [
    selectedFilters.totalPriceMin,
    selectedFilters.totalPriceMax,
    selectedFilters.pricePerSqMMin,
    selectedFilters.pricePerSqMMax,
  ]);

  const isPriceActiveInit = useMemo(() => {
    return (
      !!initialFilters.totalPriceMin ||
      !!initialFilters.totalPriceMax ||
      !!initialFilters.pricePerSqMMin ||
      !!initialFilters.pricePerSqMMax
    );
  }, [
    initialFilters.totalPriceMin,
    initialFilters.totalPriceMax,
    initialFilters.pricePerSqMMin,
    initialFilters.pricePerSqMMax,
  ]);

  const isAreaActive = useMemo(() => {
    return !!selectedFilters.areaMin || !!selectedFilters.areaMax;
  }, [selectedFilters.areaMin, selectedFilters.areaMax]);

  const isAreaActiveInit = useMemo(() => {
    return !!initialFilters.areaMin || !!initialFilters.areaMax;
  }, [initialFilters.areaMin, initialFilters.areaMax]);

  const isFloorActive = useMemo(() => {
    return (
      !!selectedFilters.floorMin ||
      !!selectedFilters.floorMax ||
      !!selectedFilters.notFirst ||
      !!selectedFilters.notLast
    );
  }, [
    selectedFilters.floorMin,
    selectedFilters.floorMax,
    selectedFilters.notFirst,
    selectedFilters.notLast,
  ]);

  const isFloorActiveInit = useMemo(() => {
    return (
      !!initialFilters.floorMin ||
      !!initialFilters.floorMax ||
      !!initialFilters.notFirst ||
      !!initialFilters.notLast
    );
  }, [
    initialFilters.floorMin,
    initialFilters.floorMax,
    initialFilters.notFirst,
    initialFilters.notLast,
  ]);

  const isRoomsActive = useMemo(() => {
    return selectedFilters?.rooms?.length;
  }, [selectedFilters.rooms]);

  const isRoomsActiveInit = useMemo(() => {
    return initialFilters?.rooms?.length;
  }, [initialFilters.rooms]);

  const isExclusiveActive = useMemo(() => {
    return selectedFilters?.exclusiveType?.length
      ? selectedFilters?.exclusiveType?.length > 0
      : false;
  }, [selectedFilters.exclusiveType]);

  const isPublicationDateActive = useMemo(() => {
    return (
      !!selectedFilters.publicationDateFrom ||
      !!selectedFilters.publicationDateTo
    );
  }, [selectedFilters.publicationDateFrom, selectedFilters.publicationDateTo]);

  const isAddedDateActive = useMemo(() => {
    return !!selectedFilters.addedDateFrom || !!selectedFilters.addedDateTo;
  }, [selectedFilters.addedDateFrom, selectedFilters.addedDateTo]);

  const isPhoneActive = useMemo(
    () => !!selectedFilters.phone,
    [selectedFilters.phone],
  );
  const isRealtorActive = useMemo(
    () => !!selectedFilters.realtorId,
    [selectedFilters.realtorId],
  );

  // AllFilters подсвечивается если ни одна из выше перечисленных не активна, но есть другие ключи
  const isAllFiltersActive = useMemo(() => {
    const unexpectedKeys = new Set([
      "dealType",
      "page",
      "limit",
      "region",
      "city",
      "district",
      "microdistrict",
      "street",
      "metroStation",
      "objectType",
      "customId",
      "titleOrDescription",
      "totalPriceMin",
      "totalPriceMax",
      "pricePerSqMMin",
      "pricePerSqMMax",
      "areaMin",
      "areaMax",
      "floorMin",
      "floorMax",
      "notFirst",
      "notLast",
      "rooms",
      "exclusiveType",
      "publicationDateFrom",
      "publicationDateTo",
      "addedDateFrom",
      "propertyType",
      "addedDateTo",
      "frame",
      "currency",
      "priceCurrency",
      // realtor simple fields that have their own icons
      "phone",
      "realtorId",
      "isGoldenVisaEligible",
    ]);
    const keys = Object.keys(selectedFilters).filter(
      (key) => unexpectedKeys.has(key) === false,
    );

    return keys.some((key) => {
      const value = (selectedFilters as any)[key];
      if (typeof value === "boolean") {
        return true;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return !!value;
    });
  }, [selectedFilters]);

  // Update selectedFilters when initialFilters change (from hook)
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      logger.info(
        "SearchPanel: Setting filters from initialFilters prop:",
        initialFilters,
      );
      setSelectedFilters(initialFilters);
    }
  }, [initialFilters]);

  return (
    <>
      <div className="w-full">
        {header && <h1 className={"head-title"}>{header}</h1>}
        {!header && (
          <h1 className={"head-title"}>{t("Easy path to your new home")}</h1>
        )}
        <div
          className={
            "grid grid-cols-2 md:flex md:flex-wrap gap-[1.5rem] items-center lg:gap-y-0 lg:space-y-[2rem]"
          }
        >
          <div className={cn("col-span-2", styles.searchPanel__typeContainer)}>
            <Link
              href={getRedirectUrlWithParams(DealType.PRIMARY, searchParams)}
              onClick={() => handleChangeDealType(DealType.PRIMARY)}
              scroll={false}
              prefetch={true}
              className={cn(
                styles.searchElement,
                styles.searchElement__type,
                activeDealType === DealType.PRIMARY && styles.selected,
                activeDealType === DealType.PRIMARY &&
                  activePopUp &&
                  styles.activePopupSelected,
              )}
            >
              {t("Developers")}
              {activeDealType === DealType.PRIMARY && (
                <IconActive className={styles.activeIcon} />
              )}
            </Link>
            <Link
              href={getRedirectUrlWithParams(DealType.BUY, searchParams)}
              onClick={() => handleChangeDealType(DealType.BUY)}
              scroll={false}
              prefetch={true}
              className={cn(
                styles.searchElement,
                styles.searchElement__type,
                activeDealType === DealType.BUY && styles.selected,
                activeDealType === DealType.BUY &&
                  activePopUp &&
                  styles.activePopupSelected,
              )}
            >
              {t("Sale")}
              {activeDealType === DealType.BUY && (
                <IconActive className={styles.activeIcon} />
              )}
            </Link>
            <Link
              href={getRedirectUrlWithParams(DealType.RENT, searchParams)}
              onClick={() => handleChangeDealType(DealType.RENT)}
              scroll={false}
              prefetch={true}
              className={cn(
                styles.searchElement,
                styles.searchElement__type,
                activeDealType === DealType.RENT && styles.selected,
                activeDealType === DealType.RENT &&
                  activePopUp &&
                  styles.activePopupSelected,
              )}
            >
              {t("Rent")}
              {activeDealType === DealType.RENT && (
                <IconActive className={styles.activeIcon} />
              )}
            </Link>
          </div>

          {/* realtor block moved to bottom */}

          <div
            ref={groupSearchBlock}
            className={cn(
              "col-span-2",
              styles.searchPanel__mainFilters,
              (activePopUp === EnumPopUpName.location ||
                activePopUp === EnumPopUpName.propertyType) &&
                styles.active,
            )}
          >
            <Feature name={FeatureFlags.SMART_AUTOCOMPLETE}>
              <Feature.Enabled>
                <SearchLocationSmart
                  initialFilters={initialFilters}
                  form={form}
                  dealType={activeDealType}
                  handleSearch={(
                    filters,
                    type?: string,
                    scrollToTop?: boolean,
                  ) =>
                    handleSearchClick(filters, type ?? "location", scrollToTop)
                  }
                  regionsListData={regionsListData}
                  watchedLocationSearch={watchedLocationSearch}
                  onClosePopup={closePopUp}
                  activePopUp={activePopUp}
                  handleShowPopup={handleShowPopup}
                  inputLocationRef={inputLocationRef}
                  groupSearchBlock={groupSearchBlock}
                  locationOptions={locationOptions}
                  onClear={handleClearSearchLocationFilter}
                  localizedLocationString={localizedLocationString}
                />
              </Feature.Enabled>
              <Feature.Disabled>
                <SearchLocationClassic
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  setSearchTitle={setSearchTitle}
                  dealType={activeDealType}
                  handleSearch={handleSearchClick}
                  filteredRegionValue={searchHelpsData?.regions || null}
                  searchTitleValue={searchTitle}
                  onClosePopup={closePopUp}
                  onSearchTitleChange={handleChangeLocation}
                  activePopUp={activePopUp}
                  handleShowPopup={handleShowPopup}
                  inputLocationRef={inputLocationRef}
                  groupSearchBlock={groupSearchBlock}
                  locationValues={locationValues}
                  isLocationActive={!!isLocationActive}
                />
              </Feature.Disabled>
            </Feature>

            <div className={styles.searchPanel__divider}></div>

            <SearchType
              isOpen={activePopUp === EnumPopUpName.propertyType}
              onOpenChange={(isOpen) =>
                isOpen
                  ? handleShowPopup(
                      isMobile ? typeLocationRef : groupSearchBlock,
                      EnumPopUpName.propertyType,
                    )
                  : closePopUp()
              }
              initialFilters={initialFilters}
              onClosePopup={closePopUp}
              dealType={activeDealType}
              handleSearch={(filters, type?: string, scrollToTop?: boolean) => {
                handleSearchClick(filters, type, scrollToTop);
                closePopUp();
              }}
              triggerButton={
                <div
                  ref={typeLocationRef}
                  className={cn(
                    styles.searchElement,
                    styles.searchElement__search,
                    activePopUp === EnumPopUpName.propertyType &&
                      "!border-red z-modal",
                  )}
                >
                  <button className={styles.Search_Panel_DropDown_btn}>
                    {selectedFilters?.objectType?.length !== 0 ? (
                      <span
                        className={cn(
                          styles.Search_Panel_DropDown_content,
                          "grow",
                        )}
                      >
                        {selectedFilters?.objectType
                          ?.map((el) => translateProperty(el))
                          .join(", ")}
                      </span>
                    ) : (
                      <span
                        className={cn(
                          styles.Search_Panel_DropDown_content,
                          "grow",
                        )}
                      >
                        {t("Property type")}
                      </span>
                    )}
                    <IconDown
                      className={cn(
                        "transition-transform rotate-0",
                        activePopUp === EnumPopUpName.propertyType &&
                          "rotate-180",
                      )}
                    />
                  </button>
                  <IconPropertyType
                    className={cn(
                      styles.searchInput__icon,
                      isPropertyTypeActive && "text-red",
                    )}
                  />
                </div>
              }
            />

            <div className={styles.searchPanel__divider}></div>

            <div className={styles.searchElement__wrapper}>
              <input
                type="text"
                className={cn(
                  styles.searchElement,
                  styles.searchElement__search,
                )}
                placeholder={t("ID / Code / Keywords / Description")}
                value={selectedText}
                onInput={(e) => handleChangeTitle(e)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchClick();
                  }
                }}
              />
              <IconKeywords
                className={cn(
                  styles.searchInput__icon,
                  isKeywordsActive && "text-red",
                )}
              />
            </div>
          </div>

          <div
            hidden
            className={"hidden lg:block lg:w-full lg:order-3"}
          ></div>

          <SearchPrice
            initialFilters={initialFilters}
            isOpen={activePopUp === EnumPopUpName.price}
            onOpenChange={(isOpen) =>
              isOpen
                ? handleShowPopup(priceRef, EnumPopUpName.price)
                : closePopUp()
            }
            onClosePopup={() => {
              closePopUp();
            }}
            dealType={activeDealType}
            handleSearch={(filters, type?: string, scrollToTop?: boolean) => {
              handleSearchClick(filters, type, scrollToTop);
              closePopUp();
            }}
            triggerButton={
              <button
                ref={priceRef}
                className={cn(
                  "order-2 lg:order-3",
                  styles.searchElement,
                  activePopUp === EnumPopUpName.price && "!border-red z-modal",
                )}
              >
                {isPriceActiveInit ? (
                  <span className="truncate font-semibold">
                    {(initialFilters.totalPriceMax ||
                      initialFilters.totalPriceMin) &&
                      formatRange(
                        initialFilters.totalPriceMin,
                        initialFilters.totalPriceMax,
                      )}
                    {(initialFilters.pricePerSqMMin ||
                      initialFilters.pricePerSqMMax) &&
                      formatRange(
                        initialFilters.pricePerSqMMin,
                        initialFilters.pricePerSqMMax,
                      )}
                    {` ${currency.symbol ?? formatCurrencySymbol(currency.label as CurrencyValue)}`}
                  </span>
                ) : (
                  <span className="truncate">{t("Price")}</span>
                )}

                <IconPrice
                  className={cn(
                    "w-[2.4rem] h-[2.4rem]",
                    isPriceActive && "text-red",
                  )}
                />
              </button>
            }
          />

          <SearchSquare
            initialFilters={initialFilters}
            isOpen={activePopUp === EnumPopUpName.area}
            onOpenChange={(isOpen) =>
              isOpen
                ? handleShowPopup(areaRef, EnumPopUpName.area)
                : closePopUp()
            }
            onClosePopup={closePopUp}
            dealType={activeDealType}
            handleSearch={(filters, type?: string, scrollToTop?: boolean) => {
              handleSearchClick(filters, type, scrollToTop);
              closePopUp();
            }}
            triggerButton={
              <button
                ref={areaRef}
                className={cn(
                  "order-2 lg:order-3",
                  styles.searchElement,
                  activePopUp === EnumPopUpName.area && "!border-red z-modal",
                )}
              >
                {isAreaActiveInit ? (
                  <span className="truncate font-semibold">
                    {formatRange(
                      initialFilters.areaMin,
                      initialFilters.areaMax,
                    )}{" "}
                    m²
                  </span>
                ) : (
                  <span className="truncate">{t("Area")}</span>
                )}

                <IconArea
                  className={cn(
                    "w-[2.4rem] h-[2.4rem]",
                    isAreaActive && "text-red",
                  )}
                />
              </button>
            }
          />

          <SearchFloor
            isOpen={activePopUp === EnumPopUpName.floor}
            onOpenChange={(isOpen) =>
              isOpen
                ? handleShowPopup(floorRef, EnumPopUpName.floor)
                : closePopUp()
            }
            initialFilters={initialFilters}
            onClosePopup={closePopUp}
            handleSearch={(filters, type?: string, scrollToTop?: boolean) => {
              handleSearchClick(filters, type, scrollToTop);
              closePopUp();
            }}
            dealType={activeDealType}
            triggerButton={
              <button
                ref={floorRef}
                className={cn(
                  "order-2 lg:order-3",
                  styles.searchElement,
                  activePopUp === EnumPopUpName.floor && "!border-red z-modal",
                )}
              >
                {isFloorActiveInit ? (
                  <span className="truncate font-semibold">
                    {summarizeFilters([
                      (initialFilters.floorMin || initialFilters.floorMax) &&
                        formatRange(
                          initialFilters.floorMin,
                          initialFilters.floorMax,
                        ),
                      initialFilters.notFirst ? t("Not the first") : undefined,
                      initialFilters.notLast ? t("Not the last") : undefined,
                    ])}
                  </span>
                ) : (
                  <span className="truncate">{t("Floor")}</span>
                )}

                <IconFloor
                  className={cn(
                    "w-[2.4rem] h-[2.4rem]",
                    isFloorActive && "text-red",
                  )}
                />
              </button>
            }
          />

          <SearchRoom
            initialFilters={initialFilters}
            onOpenChange={(isOpen) =>
              isOpen
                ? handleShowPopup(roomsRef, EnumPopUpName.rooms)
                : closePopUp()
            }
            onClosePopup={closePopUp}
            dealType={activeDealType}
            isOpen={activePopUp === EnumPopUpName.rooms}
            handleSearch={(filters, type?: string, scrollToTop?: boolean) => {
              handleSearchClick(filters, type, scrollToTop);
              closePopUp();
            }}
            triggerButton={
              <button
                ref={roomsRef}
                className={cn(
                  "order-2 lg:order-3 ",
                  styles.searchElement,
                  activePopUp === EnumPopUpName.rooms && "!border-red z-modal",
                )}
              >
                {isRoomsActiveInit ? (
                  <span className="truncate font-semibold">
                    {summarizeCountFilters(initialFilters?.rooms ?? [])}
                  </span>
                ) : (
                  <span className="truncate">{t("Rooms")}</span>
                )}

                <IconRooms
                  className={cn(
                    "w-[2.4rem] h-[2.4rem]",
                    isRoomsActive && "text-red",
                  )}
                />
              </button>
            }
          />

          <SearchAll
            initialFilters={initialFilters}
            onClosePopup={closePopUp}
            isOpen={activePopUp === EnumPopUpName.allFilters}
            onOpenChange={(isOpen) =>
              isOpen
                ? handleShowPopup(allRef, EnumPopUpName.allFilters)
                : closePopUp()
            }
            isMobile={isMobile}
            dealType={dealType}
            handleSearch={(filters, type?: string, scrollToTop?: boolean) => {
              handleSearchClick(filters, type, scrollToTop);
              closePopUp();
            }}
            triggerButton={
              <button
                ref={allRef}
                className={cn(
                  "order-2 lg:order-3 z-",
                  styles.searchElement,
                  activePopUp === EnumPopUpName.allFilters &&
                    "!border-red z-0 md:z-modal",
                )}
              >
                <span className="truncate">{t("More filters")}</span>

                <IconAllFilters
                  className={cn(isAllFiltersActive && "text-red")}
                />
              </button>
            }
          />

          {dealType.includes(DealType.PRIMARY) && hasGoldenVisa() && (
            <SearchGoldenVisa
              initialFilters={initialFilters}
              handleSearch={(filters) => {
                handleSearchClick(filters);
              }}
            />
          )}

          {/* Clear button for non-logged/non-realtor users (stage behavior) */}
          <Feature name={FeatureFlags.PROPERTIES_LIST_REALTOR_FILTERS}>
            <Feature.Enabled>
              {!isRealtor && (
                <button
                  className={cn(
                    "order-2 lg:order-3",
                    styles.searchElement,
                    styles.searchElement__clearFilter,
                  )}
                  onClick={handleClearFilter}
                >
                  <span className="truncate">{t("Clear")}</span>
                  <IconClearFilter className="w-[2rem] h-[2rem] shrink-0" />
                </button>
              )}
            </Feature.Enabled>
            <Feature.Disabled>
              <button
                className={cn(
                  "order-2 lg:order-3",
                  styles.searchElement,
                  styles.searchElement__clearFilter,
                )}
                onClick={handleClearFilter}
              >
                <span className="truncate">{t("Clear")}</span>
                <IconClearFilter className="w-[2rem] h-[2rem] shrink-0" />
              </button>
            </Feature.Disabled>
          </Feature>

          <div className={cn("col-span-2", styles.searchPanel__search)}>
            <button
              className={cn(styles.searchBtn, "grow")}
              onClick={() => handleSearchClick()}
            >
              <IconSearch className="w-[24px] h-auto" />

              <span className="truncate">{t("Search")}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Realtor-only panel at very bottom */}
      <Feature name={FeatureFlags.PROPERTIES_LIST_REALTOR_FILTERS}>
        <Feature.Enabled>
          <RealtorOnly>
            <div
              className={cn(
                "col-span-2 flex flex-wrap gap-[1.5rem] items-center mt-[1.5rem]",
              )}
            >
              <Popover
                isOpen={activePopUp === EnumPopUpName.exclusive}
                onOpenChange={(isOpen) =>
                  isOpen
                    ? handleShowPopup(exclusiveRef, EnumPopUpName.exclusive)
                    : closePopUp()
                }
              >
                <PopoverTrigger>
                  <button
                    ref={exclusiveRef}
                    className={cn(
                      styles.searchElement,
                      activePopUp === EnumPopUpName.exclusive &&
                        "!border-red z-modal",
                    )}
                  >
                    <span className="truncate">{t("Exclusive")}</span>
                    <IconExclusive
                      className={cn(
                        "w-[2.4rem] h-[2.4rem]",
                        isExclusiveActive && "text-red",
                      )}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <SearchExclusive
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    onClosePopup={closePopUp}
                    dealType={activeDealType}
                    handleSearch={(filters) => {
                      handleSearchClick(filters);
                      closePopUp();
                    }}
                  />
                </PopoverContent>
              </Popover>

              <Popover
                isOpen={activePopUp === EnumPopUpName.publicationDate}
                onOpenChange={(isOpen) =>
                  isOpen
                    ? handleShowPopup(
                        publicationDateRef,
                        EnumPopUpName.publicationDate,
                      )
                    : closePopUp()
                }
              >
                <PopoverTrigger>
                  <button
                    ref={publicationDateRef}
                    className={cn(
                      styles.searchElement,
                      activePopUp === EnumPopUpName.publicationDate &&
                        "!border-red z-modal",
                    )}
                  >
                    <span className="truncate">{t("Publication Date")}</span>
                    <IconCalendar
                      className={cn(
                        "w-[2.4rem] h-[2.4rem]",
                        isPublicationDateActive && "text-red",
                      )}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <SearchDateRange
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    onClosePopup={closePopUp}
                    dealType={activeDealType}
                    dateType="publication"
                    title={t("Publication Date")}
                    handleSearch={(filters) => {
                      handleSearchClick(filters);
                      closePopUp();
                    }}
                  />
                </PopoverContent>
              </Popover>

              <Popover
                isOpen={activePopUp === EnumPopUpName.addedDate}
                onOpenChange={(isOpen) =>
                  isOpen
                    ? handleShowPopup(addedDateRef, EnumPopUpName.addedDate)
                    : closePopUp()
                }
              >
                <PopoverTrigger>
                  <button
                    ref={addedDateRef}
                    className={cn(
                      styles.searchElement,
                      activePopUp === EnumPopUpName.addedDate &&
                        "!border-red z-modal",
                    )}
                  >
                    <span className="truncate">{t("Added Date")}</span>
                    <IconCalendar
                      className={cn(
                        "w-[2.4rem] h-[2.4rem]",
                        isAddedDateActive && "text-red",
                      )}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <SearchDateRange
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    onClosePopup={closePopUp}
                    dealType={activeDealType}
                    dateType="added"
                    title={t("Added Date")}
                    handleSearch={(filters) => {
                      handleSearchClick(filters);
                      closePopUp();
                    }}
                  />
                </PopoverContent>
              </Popover>

              <Popover
                isOpen={activePopUp === EnumPopUpName.phone}
                onOpenChange={(isOpen) =>
                  isOpen
                    ? handleShowPopup(phoneRef, EnumPopUpName.phone)
                    : closePopUp()
                }
              >
                <PopoverTrigger>
                  <button
                    ref={phoneRef}
                    className={cn(
                      styles.searchElement,
                      activePopUp === EnumPopUpName.phone &&
                        "!border-red z-modal",
                    )}
                  >
                    <span className="truncate">{t("By phone number")}</span>
                    <IconPhone
                      className={cn(
                        "w-[2.4rem] h-[2.4rem]",
                        isPhoneActive && "text-red",
                      )}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <SearchPhone
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    onClosePopup={closePopUp}
                    handleSearch={(filters) => {
                      handleSearchClick(filters);
                      closePopUp();
                    }}
                  />
                </PopoverContent>
              </Popover>

              <Popover
                isOpen={activePopUp === EnumPopUpName.realtor}
                onOpenChange={(isOpen) =>
                  isOpen
                    ? handleShowPopup(realtorRef, EnumPopUpName.realtor)
                    : closePopUp()
                }
              >
                <PopoverTrigger>
                  <button
                    ref={realtorRef}
                    className={cn(
                      styles.searchElement,
                      activePopUp === EnumPopUpName.realtor &&
                        "!border-red z-modal",
                    )}
                  >
                    <span className="truncate">{t("Realtor")}</span>
                    <IconUser
                      className={cn(
                        "w-[2.4rem] h-[2.4rem]",
                        isRealtorActive && "text-red",
                      )}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <SearchRealtor
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    onClosePopup={closePopUp}
                    handleSearch={(filters) => {
                      handleSearchClick(filters);
                      closePopUp();
                    }}
                  />
                </PopoverContent>
              </Popover>

              {/* Clear is only here for realtor; non-realtor clear is above */}
              <button
                className={cn(
                  styles.searchElement,
                  styles.searchElement__clearFilter,
                )}
                onClick={handleClearFilter}
              >
                <span className="truncate">{t("Clear")}</span>
                <IconClearFilter className="w-[2rem] h-[2rem] shrink-0" />
              </button>
            </div>
          </RealtorOnly>
        </Feature.Enabled>
      </Feature>
    </>
  );
};
